import { z } from 'zod';
import type { CreateBriefDto } from '@inference.engine/workspace-api-client/web';

/**
 * The brief as the workflow returns it, which is network JSON and parsed rather than cast.
 *
 * Looser than `CreateBriefDto` in two places on purpose: the workflow reports an absent summary and
 * an absent reference template as `null`, while the DTO omits them, so those are read as nullable
 * and dropped below.
 */
const generatedBriefSchema = z.object({
  title: z.string().min(1),
  // Dropped rather than fatal when it is not one of the known types: the create endpoint takes
  // this field as optional, and a brief type this copy of the enum has not caught up with should
  // not lose a brief that generated correctly.
  briefType: z
    .enum([
      'marketing',
      'creative',
      'design',
      'media',
      'content',
      'campaign',
      'asset_production',
      'rebrief',
      'intake',
      'approval',
    ])
    .optional()
    .catch(undefined),
  summary: z.string().nullish(),
  fields: z.record(z.string(), z.unknown()).nullish(),
  values: z.record(z.string(), z.unknown()).optional(),
  referenceTemplateId: z.string().nullish(),
  sections: z
    .array(
      z.object({
        name: z.string(),
        content: z.record(z.string(), z.unknown()).optional(),
        position: z.number(),
        required: z.boolean().optional(),
        aiAllowed: z.boolean().optional(),
        audienceIds: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  sources: z
    .array(
      z.object({
        sourceType: z.enum(['document', 'artifact', 'knowledge_base']),
        sourceId: z.string(),
        metadata: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
});

/**
 * Reads a generated brief off the workflow's output, or returns null when it is not one.
 *
 * Null rather than a throw: the caller reports a failed generation, and a run whose output cannot
 * be read is a failed generation regardless of why.
 */
export const parseGeneratedBrief = (output: unknown): CreateBriefDto | null => {
  const parsed = generatedBriefSchema.safeParse(output);
  if (!parsed.success) return null;

  const { summary, fields, referenceTemplateId, ...rest } = parsed.data;

  return {
    ...rest,
    ...(summary === null || summary === undefined ? {} : { summary }),
    ...(fields === null || fields === undefined ? {} : { fields }),
    ...(referenceTemplateId === null || referenceTemplateId === undefined
      ? {}
      : { referenceTemplateId }),
  };
};
